import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUploadCloud, FiImage, FiX, FiCheck } from 'react-icons/fi';
import './ImageUploader.css';

const ImageUploader = ({ 
  onFileSelect, 
  multiple = false, 
  maxFiles = 10,
  acceptedFormats = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
  maxSize = 10 * 1024 * 1024, // 10MB
  title = "Drop your images here",
  subtitle = "or click to browse"
}) => {
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    setError(null);
    
    if (rejectedFiles.length > 0) {
      const errorMessages = rejectedFiles.map(file => {
        if (file.errors[0]?.code === 'file-too-large') {
          return `${file.file.name} is too large`;
        }
        if (file.errors[0]?.code === 'file-invalid-type') {
          return `${file.file.name} has invalid format`;
        }
        return `${file.file.name} was rejected`;
      });
      setError(errorMessages.join(', '));
      return;
    }

    const newFiles = acceptedFiles.map(file => Object.assign(file, {
      preview: URL.createObjectURL(file)
    }));

    if (multiple) {
      const updatedFiles = [...files, ...newFiles].slice(0, maxFiles);
      setFiles(updatedFiles);
      onFileSelect(updatedFiles);
    } else {
      setFiles(newFiles.slice(0, 1));
      onFileSelect(newFiles[0]);
    }
  }, [files, multiple, maxFiles, onFileSelect]);

  const removeFile = (fileToRemove) => {
    const updatedFiles = files.filter(f => f !== fileToRemove);
    URL.revokeObjectURL(fileToRemove.preview);
    setFiles(updatedFiles);
    onFileSelect(multiple ? updatedFiles : null);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedFormats.reduce((acc, format) => {
      acc[format] = [];
      return acc;
    }, {}),
    multiple,
    maxSize,
    maxFiles
  });

  return (
    <div className="uploader-wrapper">
      <motion.div
        {...getRootProps()}
        className={`dropzone ${isDragActive ? 'active' : ''} ${files.length > 0 ? 'has-files' : ''}`}
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
      >
        <input {...getInputProps()} />
        
        <div className="dropzone-content">
          <motion.div 
            className="upload-icon"
            animate={{ y: isDragActive ? -10 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <FiUploadCloud />
          </motion.div>
          
          <h3 className="dropzone-title">{title}</h3>
          <p className="dropzone-subtitle">{subtitle}</p>
          
          <div className="format-badges">
            {['JPG', 'PNG', 'WebP', 'GIF'].map(format => (
              <span key={format} className="format-badge">{format}</span>
            ))}
          </div>
          
          <p className="dropzone-hint">
            Max {maxSize / (1024 * 1024)}MB per file
            {multiple && ` • Up to ${maxFiles} files`}
          </p>
        </div>
      </motion.div>

      <AnimatePresence>
        {error && (
          <motion.div 
            className="upload-error"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <FiX /> {error}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {files.length > 0 && (
          <motion.div 
            className="preview-grid"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            {files.map((file, index) => (
              <motion.div 
                key={file.name}
                className="preview-item"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ delay: index * 0.1 }}
              >
                <img src={file.preview} alt={file.name} />
                <div className="preview-overlay">
                  <span className="preview-name">{file.name}</span>
                  <button 
                    className="remove-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(file);
                    }}
                  >
                    <FiX />
                  </button>
                </div>
                <div className="preview-check">
                  <FiCheck />
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ImageUploader;
